import React, {useEffect, useState} from 'react';
import {Helmet} from 'react-helmet';
import DashboardHeader from '../../components/DashboardHeader';
import Wrapper from '../../components/Wrapper';
import {useTranslation} from '../../hooks/useTranslation';
import {useParams} from 'react-router-dom';
import {
  useDeleteRegionMutation,
  useGetAgencyQuery,
  useGetUsersRolesQuery,
  useInviteFboMutation,
  useLazyCorporateDetailsQuery,
  useLazyGetAgencyQuery,
  useLazyRegionsListQuery,
  useRegionsListQuery,
} from '../../store/builder/inspections.api';
import CorporateDetailsRow from '../../components/Corporate/CorporateDetailsRow';
import {useDispatch, useSelector} from 'react-redux';
import {setLoader} from '../../store/builder/reducer';
import CorporateRegions from '../../components/Corporate/CorporateRegions';
import CorporateRegionsModal from '../../components/Corporate/CorporateRegionsModal';
import Input from '../../components/Input';
import Select from 'react-select';
import {validationEmail} from '../../global/validate';
import {toast} from 'react-toastify';
import useLoader from '../../global/UseLoadingHandler';
import {useErrorHandler} from '../../global/ErrorsHandler';

const CorporateDetails = () => {
  const {t} = useTranslation();
  const dispatch = useDispatch();
  const params = useParams();
  const userInfo = useSelector(state => state?.rootReducer?.loginSlice?.user);
  const [currentUser, setCurrentUser] = useState(null);
  const [regionsStatus, setRegionsStatus] = useState(false);
  const [currentRegion, setCurrentRegion] = useState(null);
  const [inviteModal, setInviteModal] = useState(false);
  const [trigerGetUsers, setTrigerGetUsers] = useState(false);
  const [rolesOptions, setRolesOptions] = useState([]);
  const [userInviteData, setUserInviteData] = useState({
    email: '',
    role_id: null,
    agency_id: null,
    region_id: null,
    api_access_only: false,
  });

  const [
    getCorporate,
    {
      data: corporateData,
      error: corporateError,
      isLoading: corporateIsLoading,
      isSuccess: corporateIsSuccess,
      isError: corporateIsError,
    },
  ] = useLazyCorporateDetailsQuery();

  const [
    deleteRegion,
    {isLoading: deleteRegionIsLoading, isSuccess: deleteRegionIsSuccess},
  ] = useDeleteRegionMutation();

  const {
    data: userRolesData,
    isSuccess: userRolesIsSuccess,
    isLoading: userRolesIsLoading,
    isError: userRolesIsError,
    error: userRolesError,
  } = useGetUsersRolesQuery();

  const [
    getRegions,
    {
      data: regionsData,
      error: regionsError,
      isLoading: regionsIsLoading,
      isSuccess: regionsIsSuccess,
    },
  ] = useLazyRegionsListQuery();

  const {
    data: agencyData,
    error: agencyError,
    isLoading: agencyIsLoading,
    isSuccess: agencyIsSuccess,
  } = useGetAgencyQuery();

  const [
    getAgencyList,
    {
      data: agencyListData,
      isLoading: agencyListIsLoading,
      isError: agencyListIsError,
      error: agencyListError,
      isSuccess: agencyListIsSuccess,
    },
  ] = useLazyGetAgencyQuery();

  const [
    invite,
    {
      data: inviteData,
      isLoading: inviteIsLoading,
      isError: inviteIsError,
      error: inviteError,
      isSuccess: inviteIsSuccess,
    },
  ] = useInviteFboMutation();

  const [currentTab, setCurrentTab] = useState(1);

  const [errorHandler, setErrorHandler] = useErrorHandler([
    corporateError,
    userRolesError,
    regionsError,
    agencyError,
    agencyListError,
    inviteError,
  ]);
  useLoader([
    corporateIsLoading,
    regionsIsLoading,
    agencyListIsLoading,
    inviteIsLoading,
    deleteRegionIsLoading,
  ]);

  const getUsers = async () => {
    if (params?.id) {
      await getCorporate(params?.id);
    }
  };

  useEffect(() => {
    getUsers();
  }, [trigerGetUsers]);

  useEffect(() => {
    setRolesOptions(
      userRolesData
        ?.filter(
          el => el?.key == 'corporate' || el?.key == 'regional_corporate',
        )
        ?.map(role => ({
          value: role.id,
          label: role.name,
        })),
    );
  }, [userRolesData]);

  useEffect(() => {
    if (params?.id) {
      getCorporate(params?.id);
      getRegions({corporate_id: params?.id});
    }
    getAgencyList();
  }, [params?.id, deleteRegionIsSuccess]);

  useEffect(() => {
    if (corporateIsLoading) {
      dispatch(setLoader(true));
    }
    if (corporateIsSuccess) {
      dispatch(setLoader(false));
    }
    if (corporateIsError) {
      dispatch(setLoader(false));
    }
  }, [corporateIsLoading, corporateIsSuccess, corporateIsError]);

  const changeTab = tabId => setCurrentTab(tabId);
  // BE asks to filter here
  const modalRegions = agencyListData?.filter(
    agency => +agency?.corporate_id === +params?.id,
  );

  const handleNewCreateAgency = e => {
    setUserInviteData(old => ({...old, agency_id: e.value}));
  };
  const handleNewCreateEmail = e => {
    setUserInviteData(old => ({...old, email: e.target.value}));
  };
  const handleNewCreateRole = e => {
    setUserInviteData(old => ({...old, role_id: e.value}));
  };
  const handleNewCreateRegion = e => {
    setUserInviteData(old => ({...old, region_id: e.value}));
  };
  const handleNewCreateAccess = e => {
    setUserInviteData(old => ({...old, api_access_only: e.target.checked}));
  };

  const submitNewCreate = e => {
    e.preventDefault();
    if (!validationEmail(userInviteData?.email)) {
      toast.error(t('corporateDetails.messages.enterCorrectEmail'));
      return;
    }
    if (!userInviteData?.role_id) {
      toast.error(t('corporateDetails.messages.chooseRole'));
      return;
    }
    if (userInviteData?.role_id == 9 && !userInviteData?.region_id) {
      toast.error(t('corporateDetails.messages.chooseRegion'));
      return;
    }
    if (userInfo?.is_global) {
      invite({
        email: userInviteData?.email,
        role_id: userInviteData?.role_id,
        agency_id: userInviteData?.agency_id,
        region_id: userInviteData?.region_id,
        corporate_id: userInviteData?.role_id === 8 ? +params?.id : null,
      })
        .unwrap()
        .then(() => {
          toast.success(t('corporateDetails.messages.corporateInvitedSuccess'));
          setInviteModal(false);
        })
        .catch(err => {
          toast.error(err?.data?.email?.[0]);
        });
    }
  };

  const renderInviteModal = () => {
    return (
      <form className='modal'>
        <div className='modal__content'>
          <div className='modal__content-head'>
            <p>{t('corporateDetails.modals.inviteUser.title')}</p>
            <img
              className='modal__content-exit'
              src='/assets/icons/close.svg'
              alt='close'
              onClick={() => setInviteModal(false)}
            />
          </div>
          <div className='modal__content-body'>
            <Input
              containerWidth={'100%'}
              id='email'
              name='email'
              placeholder={t('corporateDetails.modals.inviteUser.email')}
              label={t('corporateDetails.modals.inviteUser.email')}
              type={'email'}
              onChange={handleNewCreateEmail}
              required={true}
            />
            <div className='customSelect'>
              <span className='customSelect__label'>{t('corporateDetails.modals.inviteUser.userRole')}</span>
              <Select
                options={rolesOptions}
                styles={{
                  control: (baseStyles, state) => ({
                    ...baseStyles,
                    borderColor: state.isFocused ? '#E9EAEB' : '#E9EAEB',
                    borderRadius: 8,
                    height: 42,
                    backgroundColor: '#F4F5F6',
                    marginTop: 8,
                  }),
                }}
                name={'role_id'}
                onChange={handleNewCreateRole}
              />
            </div>
            {userInviteData?.role_id === 9 && (
              <div className='customSelect'>
                <span className='customSelect__label'>{t('corporateDetails.modals.inviteUser.regions')}</span>
                <Select
                  options={regionsData?.map(role => ({
                    value: role.id,
                    label: role.name,
                  }))}
                  styles={{
                    control: (baseStyles, state) => ({
                      ...baseStyles,
                      borderColor: state.isFocused ? '#E9EAEB' : '#E9EAEB',
                      borderRadius: 8,
                      height: 42,
                      backgroundColor: '#F4F5F6',
                      marginTop: 8,
                    }),
                  }}
                  name={'regions_ids'}
                  onChange={handleNewCreateRegion}
                />
              </div>
            )}
            {userInviteData?.role_id === 8 && (
              <div className='customSelect'>
                <label className='checkbox-container'>
                  {t('corporateDetails.modals.inviteUser.apiAccessOnly')}
                  <input
                    type={'checkbox'}
                    id={'api_access_only'}
                    name={'api_access_only'}
                    onChange={handleNewCreateAccess}
                  />
                  <span className='checkmark'></span>
                </label>
              </div>
            )}
          </div>
          <div className='modal__content-bottom'>
            <button className='close' onClick={() => setInviteModal(false)}>
              {t('corporateDetails.modals.inviteUser.close')}
            </button>
            <button className='save' onClick={submitNewCreate}>
              {t('corporateDetails.modals.inviteUser.invite')}
            </button>
          </div>
        </div>
        <div className='modal__bg'></div>
      </form>
    );
  };

  return (
    <Wrapper>
      <Helmet>
        <title>{t('corporateDetails.pageTitle')}</title>
      </Helmet>
      <DashboardHeader
        title={t('corporateDetails.title')}
        type={
          currentTab === 4
            ? 'corporate-details-regions'
            : currentTab === 1
              ? 'corporate-details-invite'
              : 'corporate-details'
        }
        action={() => {
          if (currentTab === 4) {
            setCurrentRegion(null);
            setRegionsStatus(true);
          } else {
            setInviteModal(true);
          }
        }}
      />
      {inviteModal && renderInviteModal()}
      {regionsStatus && (
        <CorporateRegionsModal
          getRegionsList={() => getCorporate(params?.id)}
          setCurrentRegion={setCurrentRegion}
          currentRegion={currentRegion}
          exit={() => setRegionsStatus(false)}
          agencies={modalRegions}
        />
      )}
      <div className='agencyDetails'>
        <div className='agencyDetails__info'>
          <div className='agencyDetails__info-left'>
            <div className='agencyDetails__info-main'>
              <img
                className='agencyDetails__info-logo'
                src={corporateData?.details?.logo}
                alt='logo'
              />
              <div className='agencyDetails__info-text'>
                <p>{t('corporateDetails.corporateName')} </p>
                <p>{corporateData?.details?.title}</p>
              </div>
            </div>
            <p className='agencyDetails__info-info'>
              <span>{t('corporateDetails.corporateId')} </span>
              {corporateData?.details?.id}
            </p>
            <p className='agencyDetails__info-info'>
              <span>{t('corporateDetails.timezone')} </span>
              {corporateData?.details?.timezone}
            </p>
          </div>
        </div>
        <div className='agencyDetails__tabs'>
          <div
            className={`agencyDetails__tab ${currentTab === 1 ? 'active' : ''}`}
            onClick={() => changeTab(1)}
          >
            {t('corporateDetails.tabs.userManagement')}
          </div>
          <div
            className={`agencyDetails__tab ${currentTab === 2 ? 'active' : ''}`}
            onClick={() => changeTab(2)}
          >
            {t('corporateDetails.tabs.subscriptions')}
          </div>
          <div
            className={`agencyDetails__tab ${currentTab === 3 ? 'active' : ''}`}
            onClick={() => changeTab(3)}
          >
            {t('corporateDetails.tabs.fbo')}
          </div>
          <div
            className={`agencyDetails__tab ${currentTab === 4 ? 'active' : ''}`}
            onClick={() => changeTab(4)}
          >
            {t('corporateDetails.tabs.regions')}
          </div>
        </div>
        {currentTab === 1 ? (
          <div className='agencyDetails__table'>
            <div className='management__table-head corporate'>
              <div className='management__table-th'>{t('corporateDetails.tableHeaders.userManagement.id')}</div>
              <div className='management__table-th'>{t('corporateDetails.tableHeaders.userManagement.firstName')}</div>
              <div className='management__table-th'>{t('corporateDetails.tableHeaders.userManagement.lastName')}</div>
              <div className='management__table-th'>{t('corporateDetails.tableHeaders.userManagement.email')}</div>
              <div className='management__table-th'>{t('corporateDetails.tableHeaders.userManagement.role')}</div>
              <div className='management__table-th'>{t('corporateDetails.tableHeaders.userManagement.ein')}</div>
              <div className='management__table-th'>{t('corporateDetails.tableHeaders.userManagement.initials')}</div>
              <div className='management__table-th'>{t('corporateDetails.tableHeaders.userManagement.action')}</div>
            </div>
            <div className='agencyDetails__table-body'>
              {corporateData?.corporate_users?.map((user, index) => (
                <CorporateDetailsRow
                  user={user}
                  lengthArray={corporateData?.corporate_users?.length}
                  indexElem={index}
                  key={user?.id}
                  setCurrentUser={setCurrentUser}
                  currentUser={currentUser}
                  userRolesData={userRolesData}
                  type={'users'}
                  trigerGetUsers={trigerGetUsers}
                  setTrigerGetUsers={setTrigerGetUsers}
                  getUsers={getUsers}
                />
              ))}
            </div>
          </div>
        ) : currentTab === 2 ? (
          <div className='agencyDetails__table'>
            <div className='management__table-head subsc'>
              <div className='management__table-th'>{t('corporateDetails.tableHeaders.subscriptions.startDate')}</div>
              <div className='management__table-th'>{t('corporateDetails.tableHeaders.subscriptions.endDate')}</div>
              <div className='management__table-th'>{t('corporateDetails.tableHeaders.subscriptions.subscriptionCost')}</div>
              <div className='management__table-th'>
                {t('corporateDetails.tableHeaders.subscriptions.invoiceReceipt')}
              </div>
            </div>
            <div className='agencyDetails__table-body'>
              {corporateData?.subscriptions?.map((user, index) => (
                <CorporateDetailsRow
                  user={user}
                  lengthArray={corporateData?.corporate_users?.length}
                  indexElem={index}
                  key={user?.id}
                  setCurrentUser={setCurrentUser}
                  currentUser={currentUser}
                  userRolesData={userRolesData}
                  type={'subscription'}
                />
              ))}
            </div>
          </div>
        ) : currentTab === 3 ? (
          <div className='agencyDetails__table'>
            <div className='management__table-head subsc'>
              <div className='management__table-th'>{t('corporateDetails.tableHeaders.fbo.fboId')}</div>
              <div className='management__table-th'>{t('corporateDetails.tableHeaders.fbo.name')}</div>
              <div className='management__table-th'>{t('corporateDetails.tableHeaders.fbo.airportId')}</div>
              <div className='management__table-th'>{t('corporateDetails.tableHeaders.fbo.timezone')}</div>
            </div>
            <div className='agencyDetails__table-body'>
              {corporateData?.corporate_agencies?.map((user, index) => (
                <CorporateDetailsRow
                  user={user}
                  lengthArray={corporateData?.corporate_users?.length}
                  indexElem={index}
                  key={user?.id}
                  setCurrentUser={setCurrentUser}
                  currentUser={currentUser}
                  userRolesData={userRolesData}
                  type={'fbo'}
                />
              ))}
            </div>
          </div>
        ) : currentTab === 4 ? (
          <div className='agencyDetails__table'>
            <div className='management__table-head regions'>
              <div className='management__table-th'>{t('corporateDetails.tableHeaders.regions.regionId')}</div>
              <div className='management__table-th'>{t('corporateDetails.tableHeaders.regions.regionName')}</div>
              <div className='management__table-th'>{t('corporateDetails.tableHeaders.regions.fbos')}</div>
              <div className='management__table-th'>{t('corporateDetails.tableHeaders.regions.actions')}</div>
            </div>
            <div className='agencyDetails__table-body'>
              {corporateData?.regions?.map((user, index) => (
                <CorporateDetailsRow
                  user={user}
                  lengthArray={corporateData?.regions?.length}
                  indexElem={index}
                  key={user?.id}
                  setCurrentUser={setCurrentUser}
                  currentUser={currentUser}
                  userRolesData={userRolesData}
                  setRegionsStatus={setRegionsStatus}
                  setCurrentRegion={setCurrentRegion}
                  type={'region'}
                  deleteRegion={deleteRegion}
                />
              ))}
            </div>
          </div>
        ) : null}
      </div>
    </Wrapper>
  );
};

export default CorporateDetails;
